import { Form, FormControl, FormField, FormItem, FormMessage } from '@ui/form.tsx';
import {
  CoinsIcon,
  EuroIcon,
  MailIcon,
  MinusIcon,
  PartyPopperIcon,
  PlusIcon,
  TruckIcon
} from 'lucide-react';
import { Input } from '@ui/input.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import defaultAsset from '@r/assets/images/fiddy/default-image.png';
import shopDefaultBanner from '@r/assets/images/fiddy/default-shop-banner.png';
import { Button } from '@ui/button.tsx';
import { Card } from '@ui/card.tsx';
import { type ControllerRenderProps, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ChangeEvent, useCallback, useEffect } from 'react';
import { useContext, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CartContext } from '@r/context/cart-context.ts';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/dialog.tsx';
import { Link, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { ICartItemSchema } from '@r/types/zod-schemas.ts';
import { useDebounce } from '@r/hooks/use-debounce.ts';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import type {
  ProductProductReadModel,
  ShopShopReadModel
} from '@r/api/generated/client.schemas.ts';
import { getProductQuantity, getRemainingStock } from '@r/utils/product-stock.ts';

type CartItem = z.infer<typeof ICartItemSchema>;
type Product = ProductProductReadModel;
type Shop = ShopShopReadModel;

const CartInterface = ({ product, shop }: { product: Product; shop: Shop }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { addItemToCart, cartItems } = useContext(CartContext);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogData, setDialogData] = useState<{ amount: number } | null>(null);

  const isDynamicPrice = product?.dynamicPrice;
  const productDynamicQuantity = product?.dynamicQuantity || 1;
  const quantityInCart = getProductQuantity(cartItems, product.id);
  const remainingStock = getRemainingStock(product.stock, quantityInCart);
  const cannotAddToCart = remainingStock === 0;

  const FormSchema = z.object({
    amount: z
      .number({
        invalid_type_error: t('form.error.invalid.number')
      })
      .int(t('form.error.integer'))
      .min(productDynamicQuantity, t('form.error.min', { min: productDynamicQuantity }))
      .refine(
        (value) => isDynamicPrice || remainingStock == null || value <= remainingStock,
        t('cart.error.stock.insufficient')
      )
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: { amount: product?.dynamicPrice ? productDynamicQuantity : 1 },
    mode: 'onChange'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const amount = form.watch('amount');

  const isDynamicQuantityMultiple = useCallback(
    (amount: number) => {
      return amount % productDynamicQuantity === 0;
    },
    [productDynamicQuantity]
  );

  const adjustValue = useCallback(
    (value: number) => {
      // Adjust the value to be a multiple of productDynamicQuantity
      const adjusted = Math.floor(value / productDynamicQuantity) * productDynamicQuantity;

      return Math.max(adjusted, productDynamicQuantity);
    },
    [productDynamicQuantity]
  );

  const normalizeAmount = useCallback(
    (value: number) => {
      if (!isDynamicPrice || isDynamicQuantityMultiple(value)) {
        return value;
      }

      return adjustValue(value);
    },
    [adjustValue, isDynamicPrice, isDynamicQuantityMultiple]
  );

  const normalizedAmount = useMemo(() => {
    if (!isDynamicPrice) {
      return amount;
    }

    return normalizeAmount(amount);
  }, [amount, isDynamicPrice, normalizeAmount]);

  const isWaitingForRoundedAmount = isDynamicPrice && amount !== normalizedAmount;

  const pointValue = useMemo(() => {
    if (product?.dynamicPrice) {
      return (normalizedAmount / (product.dynamicQuantity ?? 1)) * product.pointUnitPrice;
    }

    return amount * product.pointUnitPrice;
  }, [amount, normalizedAmount, product]);

  const handleIncrement = () => {
    if (!isDynamicPrice && remainingStock != null && amount >= remainingStock) return;

    if (!form.formState.isValid) {
      form.setValue('amount', productDynamicQuantity);
      form.trigger('amount');
    } else {
      form.setValue('amount', amount + productDynamicQuantity);
    }
  };

  const handleDecrement = () => {
    if (amount > 1) {
      form.setValue('amount', amount - productDynamicQuantity);
    }
  };

  const handleSubmit = async (data: z.infer<typeof FormSchema>) => {
    setIsSubmitting(true);
    try {
      if (product) {
        const finalAmount = product.dynamicPrice ? normalizeAmount(data.amount) : data.amount;
        const cartItem = {
          iri: router.state.location.pathname,
          product: product as CartItem['product'],
          quantity: product.dynamicPrice ? 1 : finalAmount,
          addedAt: Date.now()
        } as CartItem;

        if (product.dynamicPrice) {
          cartItem.dynamicPriceAmount = finalAmount;
        }

        const result = await addItemToCart(cartItem);
        if (!result.success) {
          toast.error(result.error ?? t('cart.error.stock.insufficient'));
          return;
        }
        setDialogData({ amount: finalAmount });
        setShowDialog(true);
      }
    } catch (error) {
      console.error(error);
      toast.error(t('shop.product.error.add.to.cart'));
    } finally {
      setIsSubmitting(false);
      form.setValue('amount', productDynamicQuantity);
    }
  };

  const handleContinueShopping = () => {
    setShowDialog(false);
  };

  const debouncedValue = useDebounce(amount, 1500);

  const handleAmountChange = (
    e: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<z.infer<typeof FormSchema>, 'amount'>
  ) => {
    const value = e.target.value;

    if (value === '') {
      field.onChange('');
    } else {
      const parsedValue = Number.parseInt(value, 10);

      if (!Number.isNaN(parsedValue) && parsedValue >= 1) {
        field.onChange(parsedValue);
      }
    }
  };

  useEffect(() => {
    if (debouncedValue && isDynamicPrice && debouncedValue !== normalizeAmount(debouncedValue)) {
      form.setValue('amount', adjustValue(debouncedValue));
      toast.warning(
        t('form.warning.dynamic.quantity', {
          dynamicQuantity: productDynamicQuantity
        })
      );
    }
  }, [
    adjustValue,
    debouncedValue,
    form,
    isDynamicPrice,
    normalizeAmount,
    productDynamicQuantity,
    t
  ]);

  return (
    <>
      <Card className="sticky top-10 p-5 rounded-none xl:top-5 bg-body sm:bg-background sm:rounded-3xl xl:border-0 xl:p-10">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-3 md:space-y-0 md:space-x-2 xl:space-x-0 xl:space-y-5 md:flex md:items-end xl:block"
          >
            <div className="flex gap-x-2 items-end md:w-2/3 xl:w-full xl:flex-wrap xl:gap-x-0 xl:gap-y-5">
              <h1 className="hidden mb-2 w-full text-2xl font-semibold text-center xl:block">
                {t('shop.product.add.to.cart.title')}
              </h1>

              {!isDynamicPrice && (
                <div className="w-1/2 xl:w-full xl:pt-5">
                  <div className="flex flex-wrap items-center justify-center gap-x-2.5 mb-1.5 xl:mb-2.5">
                    <div className="flex items-center gap-x-2.5">
                      <CoinsIcon size="25" />
                      <p className="text-xl font-bold xl:text-2xl">
                        {FormattedNumber({ value: pointValue, style: 'decimal' })}
                      </p>
                    </div>

                    <p className="text-xl font-bold xl:text-2xl">
                      {pointsTranslation(t, pointValue)}
                    </p>
                  </div>
                </div>
              )}

              <div className="w-1/2 xl:w-full">
                <div className="flex items-center space-x-1 xl:space-x-2.5 text-muted-foreground mb-1.5 xl:mb-2.5">
                  {isDynamicPrice ? (
                    <>
                      <EuroIcon size="18" className="hidden xl:block" />
                      <p className="text-xs xl:text-sm">{t('shop.product.select.value')}&nbsp;:</p>
                    </>
                  ) : (
                    <p className="text-xs xl:text-sm">{t('shop.product.select.quantity')}&nbsp;:</p>
                  )}
                </div>

                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem className="text-center">
                      <FormControl>
                        <div className="flex justify-between items-center p-1 w-full h-10 rounded-full border bg-background sm:bg-body border-1 border-background">
                          <button
                            type="button"
                            onClick={handleDecrement}
                            className="flex justify-center items-center h-full rounded-full transition ease-in-out cursor-pointer hover:bg-muted-foreground/30 hover:text-background aspect-square"
                          >
                            <MinusIcon size="18" />
                          </button>
                          <Input
                            type="text"
                            className="h-full text-center bg-transparent rounded-none border-none"
                            value={field.value}
                            onChange={(e) => handleAmountChange(e, field)}
                            {...(isDynamicPrice && {
                              suffix: '€'
                            })}
                          />
                          <button
                            type="button"
                            onClick={handleIncrement}
                            className="flex justify-center items-center h-full rounded-full transition ease-in-out cursor-pointer hover:bg-muted-foreground/30 hover:text-background aspect-square"
                          >
                            <PlusIcon size="18" />
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage className="mt-2" />
                    </FormItem>
                  )}
                />
              </div>

              {isDynamicPrice && (
                <div className="w-1/2 xl:w-full">
                  <div className="flex items-center space-x-1 xl:space-x-2.5 text-muted-foreground mb-1.5 xl:mb-2.5">
                    <CoinsIcon size="18" className="hidden xl:block" />
                    <p className="text-xs xl:text-sm">
                      {t('shop.product.cost', { currency: t('point.name_other') })}
                    </p>
                  </div>

                  <div className="flex justify-center items-center px-3 py-1 h-10 text-center rounded-full border border-1">
                    {pointsTranslation(t, pointValue, true)}
                  </div>
                </div>
              )}

              <div className="hidden xl:flex items-center gap-x-2.5 text-muted-foreground pt-5">
                {product?.isDemat ? <MailIcon size={20} /> : <TruckIcon size={20} />}

                <p className="text-sm">
                  {product?.isDemat
                    ? t('shop.product.delivery.instant.email')
                    : t('shop.product.delivery.standard')}
                </p>
              </div>
            </div>

            <div className="flex h-10 md:w-1/3 xl:w-full">
              <img
                className="object-cover object-center mr-2 h-full rounded-full aspect-square md:hidden"
                src={shop.logo?.pathUri || defaultAsset}
                alt="Shop logo"
                onError={(e) => {
                  e.currentTarget.src = shopDefaultBanner;
                  e.currentTarget.onerror = null;
                }}
              />

              <Button
                className="w-full font-medium"
                rounded={'full'}
                type="submit"
                disabled={
                  !form.formState.isValid ||
                  isSubmitting ||
                  isWaitingForRoundedAmount ||
                  cannotAddToCart
                }
              >
                {t('shop.product.add.to.cart.button')}
              </Button>
            </div>
          </form>
        </Form>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent
          className="p-10 !rounded-3xl"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="gap-2.5">
            <PartyPopperIcon className="mx-auto" size={40} />
            <DialogTitle className="text-xl text-center">
              {t('shop.product.added.to.cart.success')}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-2 text-muted-foreground">
            <p>
              {t('shop.product.item.label')}
              <span className="font-medium text-foreground">{product?.name}</span>
            </p>
            {product?.dynamicPrice && dialogData?.amount && (
              <p>
                <span className="font-medium text-foreground">
                  {t('item.dynamic.price', {
                    amount: dialogData.amount,
                    currency: '€'
                  })}
                </span>
              </p>
            )}
            <p>
              {t('shop.product.quantity.label')}
              <span className="font-medium text-foreground">
                {product?.dynamicPrice ? 1 : dialogData?.amount}
              </span>
            </p>
          </div>

          <DialogFooter>
            <div className="flex flex-col justify-center space-y-5 w-full">
              <Link to="/cart">
                <Button rounded="full" className="w-full">
                  {t('shop.product.go.to.cart')}
                </Button>
              </Link>

              <Button rounded="full" variant="outline" onClick={handleContinueShopping}>
                {t('shop.product.continue.shopping')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export { CartInterface };
